import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Link from '@mui/material/Link';
import Typography from '@mui/material/Typography';
import { SystemStyleObject } from '@mui/system/styleFunctionSx/styleFunctionSx';
import React from 'react';
import { useNavigate, useOutletContext, useParams, useSearchParams } from 'react-router-dom';

import { ApplicationModal } from '@/components/organisms/ApplicationModal';
import { useLocalizedFormatter } from '@/hooks/useLocalizedFormatter';
import { LocaleContext, useTranslator } from '@/hooks/useTranslator';
import { CustomerDepositOutletContext } from '@/Screens/Dashboard/Customers/CustomerDepositFlow';
import { CryptoCompletedModal } from '@/Screens/Dashboard/Flows/common/CryptoCompletedModal';
import { Locale } from '@/types/locale';
import { getCurrencyName } from '@/utility/formatAmount';

export const CustomerDepositCompleted: React.FC = (): React.ReactElement => {
  const tr = useTranslator();
  const [searchParams] = useSearchParams();
  const isCryptoCompletion = searchParams.get('crypto') === 'true';

  // For crypto deposits, show simplified completion modal (no outlet context needed)
  if (isCryptoCompletion) {
    return (
      <CryptoCompletedModal
        title={tr('CRYPTO_DEPOSIT_COMPLETED_TITLE')}
        description={tr('CRYPTO_DEPOSIT_COMPLETED_DESCRIPTION')}
      />
    );
  }

  // For non-crypto deposits, render the full completion screen
  return <FiatCustomerDepositCompleted />;
};

const FiatCustomerDepositCompleted: React.FC = (): React.ReactElement => {
  const { reducer } = useOutletContext<CustomerDepositOutletContext>();
  const [state] = reducer;
  const { beneficiaryId = '' } = useParams<{ readonly beneficiaryId: string }>();
  const navigate = useNavigate();

  const { toAmount, toCurrency } = state;

  const locale = React.useContext<Locale>(LocaleContext);
  const formatter = useLocalizedFormatter({
    maximumFractionDigits: 2,
    minimumFractionDigits: 2,
  });
  const tr = useTranslator();

  const onClose = React.useCallback((): void => {
    navigate(`/accounts/${beneficiaryId}`, { replace: true });
  }, [navigate, beneficiaryId]);

  const currencyName = React.useMemo((): string => {
    return getCurrencyName(locale, toCurrency);
  }, [toCurrency, locale]);

  const textVariableFont = React.useMemo((): number => {
    const length = currencyName.length;

    if (length <= 10) {
      return 2.5;
    }

    if (length >= 20) {
      return 1;
    }

    return Math.round((-1.25 * length + 41) / 16);
  }, [currencyName]);

  return (
    <ApplicationModal
      title={tr('YOUR_FUNDS_HAVE_BEEN_ADDED')}
      description={tr('CHECK_YOUR_PAYMENT_HISTORY_TO_TRACK_STATUS')}
      onClose={onClose}>
      <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center">
        <Box sx={styles.amountContainer}>
          <Typography fontSize={12} fontWeight={400} lineHeight="20px" color="custom.LightGray">
            {tr('CALIZA_WALLET_BALANCE')}
          </Typography>
          <Box sx={styles.priceCurrencyPairContainer}>
            <Typography
              component="span"
              fontSize={40}
              fontWeight={500}
              lineHeight="48px"
              color="text.primary">
              ${formatter.format(toAmount ?? 0)}
            </Typography>
            <Typography
              title={currencyName}
              sx={styles.currency}
              component="span"
              fontSize={`${textVariableFont}rem`}
              fontWeight={500}
              lineHeight="48px"
              color="custom.LightGray">
              {' '}
              {currencyName}
            </Typography>
          </Box>
        </Box>
        <Box width="100%" mt={6}>
          <Button variant="dark" fullWidth={true} onClick={onClose}>
            {tr('DONE')}
          </Button>
          <Box sx={styles.cancelButton}>
            <Link onClick={onClose}>
              <Typography fontSize={16} fontWeight={400} lineHeight="24px">
                {tr('BACK_TO_CUSTOMER')}
              </Typography>
            </Link>
          </Box>
        </Box>
      </Box>
    </ApplicationModal>
  );
};

const styles: Record<string, SystemStyleObject> = {
  priceCurrencyPairContainer: {
    maxWidth: '100%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    gap: 1,
  },
  currency: {
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  cancelButton: {
    mt: 3,
    display: 'flex',
    justifyContent: 'center',
  },
  amountContainer: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    gap: 0.5,
    width: 513,
    height: 120,
    textAlign: 'center',
    borderRadius: 4,
    bgcolor: 'background.paper',
    borderWidth: 1,
    borderStyle: 'solid',
    borderColor: 'custom.DividerLight',
    mt: 1,
    px: 2,
  },
};
